import React, { useRef, useEffect, useState } from 'react'
import { Send, Mic, MicOff } from 'lucide-react'
import { renderMarkdown } from '../utils/markdown'
import { useVoiceTranscription } from '../hooks/useVoiceTranscription'

export interface ChatMessage {
    id?: number
    role: "system" | "user" | "assistant"
    content: string
    renderedContent?: string
    pending?: boolean
    personaId?: number
    personaName?: string
    messageType?: string
}

interface ChatInterfaceProps {
    messages: ChatMessage[]
    onSendMessage: (message: string) => void
    isLoading?: boolean
    disabled?: boolean
    placeholder?: string
    className?: string
    renderMessage?: (message: ChatMessage, index: number) => React.ReactNode
    typingIndicator?: React.ReactNode
    headerContent?: React.ReactNode
    footerContent?: React.ReactNode
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
    messages,
    onSendMessage,
    isLoading = false,
    disabled = false,
    placeholder = "Type your message...",
    className = "",
    renderMessage,
    typingIndicator,
    headerContent,
    footerContent,
}) => {
    const [inputMessage, setInputMessage] = useState("")
    const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
    const [voiceSessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)
    const chatContainerRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const messageEndRef = useRef<HTMLDivElement>(null)
    
    // Voice transcription hook
    const voice = useVoiceTranscription(voiceSessionId)

    // Default message renderer
    const defaultRenderMessage = (message: ChatMessage, index: number) => {
        const isUser = message.role === "user"
        const isSystem = message.role === "system"
        
        return (
            <div key={index} className={`mb-4 ${isUser ? "text-right" : ""}`}>
                <div className={`inline-block p-3 rounded-lg text-base ${
                    isUser 
                        ? "bg-action text-plum rounded-br-none" 
                        : isSystem
                            ? "bg-gray-50 text-gray-600 w-full border-l-4 border-gray-300"
                            : "bg-gray-100 text-gray-800 w-full"
                } ${message.pending ? "opacity-70" : ""}`}>
                    {message.personaName && !isSystem && (
                        <div className="font-semibold text-sm text-amber-900 mb-1">
                            {message.personaName}
                        </div>
                    )}
                    {isUser ? (
                        <div>{message.content}</div>
                    ) : (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: message.renderedContent || renderMarkdown(message.content)
                            }}
                            className={`prose max-w-none whitespace-pre-wrap ${
                                isSystem ? "prose-sm text-sm text-gray-600 italic font-light" : "prose-base"
                            }`}
                            style={isSystem ? {fontStyle: 'italic'} : {}}
                        />
                    )}
                    {message.pending && (
                        <span className="inline-block animate-pulse ml-1">▋</span>
                    )}
                </div>
            </div>
        )
    }

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current
        if (!textarea) return

        const hasMessages = messages.filter(m => m.role === 'user' || m.role === 'assistant').length > 0
        
        // Store previous height to calculate difference
        const previousHeight = textarea.offsetHeight
        
        // Reset height to get accurate scrollHeight
        textarea.style.height = 'auto'
        
        // Calculate new height
        const newHeight = Math.min(textarea.scrollHeight, 200) // Max height of 200px
        textarea.style.height = `${newHeight}px`
        
        // If in existing chat and textarea grew, scroll to keep it in view
        if (hasMessages && newHeight > previousHeight) {
            const heightDiff = newHeight - previousHeight
            window.scrollBy(0, heightDiff)
        }
    }

    useEffect(() => {
        adjustTextareaHeight()
    }, [inputMessage])

    // Focus the textarea once it's enabled. Mount-time focus runs before
    // `disabled` flips false on chats that need a websocket handshake first
    // (e.g. the freshly-seeded "Discuss" chat), so we re-fire whenever the
    // disabled/loading state clears.
    useEffect(() => {
        if (!disabled && !isLoading) {
            textareaRef.current?.focus()
        }
    }, [disabled, isLoading])

    // Handle scroll position
    useEffect(() => {
        const container = chatContainerRef.current
        if (!container) return

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 10
            setIsUserScrolledUp(!isAtBottom)
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [])

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (!isUserScrolledUp && messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages, isUserScrolledUp])

    const handleSendMessage = () => {
        if (inputMessage.trim() && !disabled && !isLoading) {
            // Stop recording if it's active
            if (voice.state.isRecording) {
                voice.stopRecording()
            }
            onSendMessage(inputMessage)
            setInputMessage("")
            voice.clearText()  // Clear accumulated voice text after sending
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // On desktop: Enter sends message, Shift+Enter adds newline
        // On mobile: Enter adds newline (natural behavior)
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

        if (e.key === "Enter" && !e.shiftKey && !isMobile) {
            e.preventDefault()
            handleSendMessage()
        }
        // On mobile, let Enter key work naturally (add newline)
    }

    // Handle voice transcription updates
    useEffect(() => {
        if (voice.state.finalText) {
            console.log('VOICE DEBUG: finalText received:', voice.state.finalText)
            
            // Show the accumulated transcribed text in input
            setInputMessage(voice.state.finalText)
            // DON'T clear voice text here - let it accumulate across pauses
        }
    }, [voice.state.finalText])

    const handleVoiceToggle = () => {
        console.log('Voice toggle clicked, current state:', voice.state)
        if (voice.state.isRecording) {
            console.log('Stopping recording...')
            voice.stopRecording()
            // Focus the input box when stopping recording
            setTimeout(() => {
                const textarea = textareaRef.current
                if (textarea) {
                    textarea.focus()
                    // Set cursor to end of text
                    const length = textarea.value.length
                    textarea.setSelectionRange(length, length)
                }
            }, 100)
        } else {
            console.log('Starting recording...')
            voice.startRecording(inputMessage)  // Pass current input text
        }
    }

    // Get display text (input + current transcription)
    const displayText = inputMessage + 
        (voice.state.currentText ? (inputMessage ? ' ' : '') + voice.state.currentText : '')

    return (
        <div className={`flex flex-col ${className}`}>
            {headerContent}
            
            {/* Grows to fill whatever height the page gives this column, so
                the composer sits at the bottom of a short chat instead of
                right under its last message. A long chat grows past it and
                the page scrolls. */}
            <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4"
            >
                {messages.map((message, index) => {
                    const customRender = renderMessage ? renderMessage(message, index) : null
                    return customRender || defaultRenderMessage(message, index)
                })}
                
                {typingIndicator}
                
                <div ref={messageEndRef} />
            </div>

            <div className="p-4 shell-safe-bottom">
                {footerContent}
                
                <div className="relative border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-action">
                    {voice.state.error && (
                        <div className="absolute -top-8 left-0 right-0 text-sm text-rose-600 bg-rose-50 px-2 py-1 rounded border border-rose-200">
                            {voice.state.error}
                        </div>
                    )}
                    <textarea
                        ref={textareaRef}
                        placeholder={voice.state.isRecording ? "Listening..." : placeholder}
                        className={`w-full p-2 pr-20 pb-10 text-base focus:outline-none resize-none overflow-hidden bg-transparent ${
                            voice.state.isRecording ? 'bg-rose-50' : ''
                        }`}
                        value={displayText}
                        onChange={(e) => {
                            // Only allow editing if not currently recording
                            if (!voice.state.isRecording) {
                                setInputMessage(e.target.value)
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        onClick={() => {
                            // Stop recording when clicking on textarea
                            if (voice.state.isRecording) {
                                voice.stopRecording()
                            }
                        }}
                        disabled={disabled || isLoading}
                        rows={1}
                        style={{ minHeight: '40px' }}
                    />
                    {voice.state.isProcessing && (
                        <div className="absolute bottom-12 right-2 text-xs text-gray-500">
                            Processing...
                        </div>
                    )}
                    <div className="absolute bottom-2 right-2 flex gap-1">
                        <button
                            className={`p-2 rounded-lg transition-colors ${
                                voice.state.isRecording
                                    ? 'bg-danger hover:bg-danger text-white animate-pulse'
                                    : voice.state.isConnecting
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                            }`}
                            onClick={handleVoiceToggle}
                            disabled={disabled || isLoading || voice.state.isConnecting}
                            title={voice.state.isRecording ? 'Stop recording' : 'Start voice input'}
                        >
                            {voice.state.isRecording ? <MicOff size={20} /> : <Mic size={20} />}
                        </button>
                        <button
                            className="bg-action hover:bg-action-dark text-plum p-2 rounded-lg disabled:opacity-50 disabled:hover:bg-action transition-colors"
                            onClick={handleSendMessage}
                            disabled={!inputMessage.trim() || disabled || isLoading}
                        >
                            <Send size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ChatInterface